/**
 * useRoutingRules Hook
 *
 * Manages routing rules state with TanStack Query for server state.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { rankDeliveryRules } from '@aica/core';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { useToast } from '@/hooks/use-toast';
import type {
  DeliveryRule,
  DeliveryRulesListResponse,
  CreateDeliveryRuleRequest,
  UpdateDeliveryRuleRequest,
  ReorderResponse,
} from '@/lib/api/types';

// Query keys for routing rules
export const routingRulesKeys = {
  all: (selectedOrgId: string | null) => ['routing-rules', selectedOrgId] as const,
  lists: (selectedOrgId: string | null) =>
    [...routingRulesKeys.all(selectedOrgId), 'list'] as const,
  list: (selectedOrgId: string | null, filters: { projectId?: string }) =>
    [...routingRulesKeys.lists(selectedOrgId), filters] as const,
  details: (selectedOrgId: string | null) =>
    [...routingRulesKeys.all(selectedOrgId), 'detail'] as const,
  detail: (selectedOrgId: string | null, id: string) =>
    [...routingRulesKeys.details(selectedOrgId), id] as const,
  entityTypes: (selectedOrgId: string | null) =>
    [...routingRulesKeys.all(selectedOrgId), 'entity-types'] as const,
};

interface UseRoutingRulesOptions {
  projectId?: string;
  enabled?: boolean;
}

/**
 * Hook for fetching delivery rules list
 */
export function useRoutingRules(options: UseRoutingRulesOptions = {}) {
  const { projectId, enabled = true } = options;
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: routingRulesKeys.list(selectedOrgId, { projectId }),
    queryFn: () => apiClient.deliveryRules.getDeliveryRules({ projectId }),
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === selectedOrgId ? previousData : undefined,
  });
}

/**
 * Hook for fetching single delivery rule
 */
export function useRoutingRule(id: string, enabled = true) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: routingRulesKeys.detail(selectedOrgId, id),
    queryFn: () => apiClient.deliveryRules.getDeliveryRuleById(id),
    enabled: enabled && !!id && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching CRM entity types
 */
export function useEntityTypes() {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: routingRulesKeys.entityTypes(selectedOrgId),
    queryFn: () => apiClient.deliveryRules.getEntityTypes(),
    enabled: !!selectedOrgId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for creating routing rule
 */
export function useCreateRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<DeliveryRule, Error, CreateDeliveryRuleRequest>({
    mutationFn: (data: CreateDeliveryRuleRequest) =>
      apiClient.deliveryRules.createDeliveryRule(data),
    onSuccess: (newRule: DeliveryRule) => {
      queryClient.setQueryData(
        routingRulesKeys.detail(selectedOrgId, newRule.id),
        newRule
      );
      // Keep create path as invalidate because list membership depends on active filters.
      void queryClient.invalidateQueries({
        queryKey: routingRulesKeys.lists(selectedOrgId),
      });
      toast({
        title: t('routing.toast.deliveryRuleCreated', 'Delivery rule created'),
        description: t(
          'routing.toast.ruleCreatedDescription',
          'Rule "{{name}}" has been created.'
        ).replace('{{name}}', newRule.name),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.deliveryRuleCreateFailed',
          'Failed to create delivery rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for updating routing rule
 */
export function useUpdateRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<
    DeliveryRule,
    Error,
    { id: string; data: UpdateDeliveryRuleRequest }
  >({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeliveryRuleRequest }) =>
      apiClient.deliveryRules.updateDeliveryRule(id, data),
    onSuccess: (updatedRule: DeliveryRule) => {
      queryClient.setQueryData(
        routingRulesKeys.detail(selectedOrgId, updatedRule.id),
        updatedRule
      );
      queryClient.setQueriesData<DeliveryRulesListResponse>(
        { queryKey: routingRulesKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          let changed = false;
          const nextRules = previous.data.map((rule) => {
            if (rule.id !== updatedRule.id) return rule;
            changed = true;
            return updatedRule;
          });
          return changed ? { ...previous, data: nextRules } : previous;
        }
      );
      toast({
        title: t('routing.toast.deliveryRuleUpdated', 'Delivery rule updated'),
        description: t(
          'routing.toast.ruleUpdatedDescription',
          'Rule "{{name}}" has been updated.'
        ).replace('{{name}}', updatedRule.name),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.deliveryRuleUpdateFailed',
          'Failed to update delivery rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for deleting delivery rule
 */
export function useDeleteRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id: string) => apiClient.deliveryRules.deleteDeliveryRule(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueriesData<DeliveryRulesListResponse>(
        { queryKey: routingRulesKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          const nextRules = previous.data.filter((rule) => rule.id !== deletedId);
          if (nextRules.length === previous.data.length) return previous;
          return {
            ...previous,
            data: nextRules,
            meta: { ...previous.meta, total: Math.max(0, previous.meta.total - 1) },
          };
        }
      );
      queryClient.removeQueries({
        queryKey: routingRulesKeys.detail(selectedOrgId, deletedId),
        exact: true,
      });
      toast({
        title: t('routing.toast.deliveryRuleDeleted', 'Delivery rule deleted'),
        description: t(
          'routing.toast.deliveryRuleDeletedDescription',
          'The delivery rule has been deleted.'
        ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.deliveryRuleDeleteFailed',
          'Failed to delete delivery rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for toggling delivery rule enabled status
 */
export function useToggleRoutingRule() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<DeliveryRule, Error, string>({
    mutationFn: (id: string) => apiClient.deliveryRules.toggleDeliveryRule(id),
    onSuccess: (updatedRule: DeliveryRule) => {
      queryClient.setQueryData(
        routingRulesKeys.detail(selectedOrgId, updatedRule.id),
        updatedRule
      );
      queryClient.setQueriesData<DeliveryRulesListResponse>(
        { queryKey: routingRulesKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          let changed = false;
          const nextRules = previous.data.map((rule) => {
            if (rule.id !== updatedRule.id) return rule;
            changed = true;
            return updatedRule;
          });
          return changed ? { ...previous, data: nextRules } : previous;
        }
      );
      toast({
        title: updatedRule.isEnabled
          ? t('routing.toast.ruleEnabled', 'Rule enabled')
          : t('routing.toast.ruleDisabled', 'Rule disabled'),
        description: t(
          'routing.toast.ruleToggleDescription',
          'Rule "{{name}}" is now {{status}}.'
        )
          .replace('{{name}}', updatedRule.name)
          .replace(
            '{{status}}',
            updatedRule.isEnabled
              ? t('routing.toast.enabled', 'enabled')
              : t('routing.toast.disabled', 'disabled')
          ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.deliveryRuleToggleFailed',
          'Failed to toggle delivery rule'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Hook for reordering delivery rules
 */
export function useReorderRoutingRules() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation<ReorderResponse, Error, { projectId: string; ruleIds: string[] }>({
    mutationFn: ({ projectId, ruleIds }: { projectId: string; ruleIds: string[] }) =>
      apiClient.deliveryRules.reorderDeliveryRules({ projectId, ruleIds }),
    onSuccess: (_, variables) => {
      // The same formula the server writes (`rankDeliveryRules`, #2362): the
      // listed rules first, the rest of the project after them, every one on the
      // `(position + 1) * step` scale (`deliveryRulePriorityStep`: 10, narrower
      // for a project of more than 1000 rules, never above the API maximum).
      // Rules of other projects (an org-wide list) are not part of this ranking.
      queryClient.setQueriesData<DeliveryRulesListResponse>(
        { queryKey: routingRulesKeys.lists(selectedOrgId) },
        (previous) => {
          if (!previous) return previous;
          // A paged or partial slice cannot be ranked against the server's
          // numbering — it ranks the whole project. The refetch below settles it.
          if (previous.data.length < previous.meta.total) return previous;

          const projectRules = previous.data.filter(
            (rule) => rule.projectId === variables.projectId
          );
          if (!projectRules.some((rule) => variables.ruleIds.includes(rule.id))) {
            return previous;
          }

          const ranked = new Map(
            rankDeliveryRules(projectRules, variables.ruleIds).map((rule) => [
              rule.id,
              rule,
            ])
          );
          const nextRules = previous.data
            .map((rule) => ranked.get(rule.id) ?? rule)
            .sort((a, b) => a.priority - b.priority);

          return { ...previous, data: nextRules };
        }
      );
      // The cache is only a guess at what the server wrote; refetch settles it.
      void queryClient.invalidateQueries({
        queryKey: routingRulesKeys.lists(selectedOrgId),
      });
      toast({
        title: t('routing.toast.rulesReordered', 'Rules reordered'),
        description: t(
          'routing.toast.deliveryRulesReorderedDescription',
          'Delivery rule priority has been updated.'
        ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('routing.toast.rulesReorderFailed', 'Failed to reorder rules'),
        description: error.message,
        variant: 'error',
      });
    },
  });
}
